import { useEffect, useState } from "react";
import { UserName } from "@/components/badges/UserName";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import { useUserRoles } from "@/hooks/useUserRoles";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Check, X, Calendar, MessageSquare, RefreshCw, Hand, Users, Handshake } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format, isFuture } from "date-fns";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { MessageThreadsList } from "@/components/connections/MessageThreadsList";
import { useThreadUnreadCount, useGetOrCreateThread } from "@/hooks/useMessageThreads";
import { ThreadMessagingPanel } from "@/components/connections/ThreadMessagingPanel";
import { usePendingHelpRequestCount } from "@/hooks/useAllHelpRequests";
import { HelpRequestsList } from "@/components/connections/HelpRequestsList";
import { MentorshipRequestsList } from "@/components/mentorship/MentorshipRequestsList";
import { SentMentorshipRequestsList } from "@/components/mentorship/SentMentorshipRequestsList";
import { ReceivedMentorOffersList } from "@/components/mentorship/ReceivedMentorOffersList";
import { usePendingMentorshipRequestCount, usePendingMentorOfferCount } from "@/hooks/useMentorshipRequests";
import { CommunityConnectionsSection } from "@/components/connections/CommunityConnectionsSection";
import { useReceivedConnectionRequests } from "@/hooks/useCommunityConnections";

interface ConnectionRequest {
  id: string;
  status: string;
  message: string | null;
  scheduled_at: string | null;
  zoom_link: string | null;
  created_at: string;
  mentorship_completed_at?: string | null;
  admin_rejection_reason_public?: string | null;
  entrepreneur: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    email?: string;
  };
  mentor: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    email?: string;
  };
}

export default function Connections() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlThreadId = searchParams.get("threadId");
  const urlAction = searchParams.get("action");
  // "Message" menu items navigate to /connections?openMessage=<userId>. That
  // param was never read, so those links were a no-op.
  const urlOpenMessage = searchParams.get("openMessage");
  const { user, loading: authLoading } = useAuth();

  const [capturedThreadId, setCapturedThreadId] = useState<string | null>(null);
  const [handledOpenMessage, setHandledOpenMessage] = useState<string | null>(null);
  const getOrCreateThread = useGetOrCreateThread();
  const [capturedAction, setCapturedAction] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("community");

  // Capture thread ID and action from URL → switch to messages tab
  useEffect(() => {
    if (urlThreadId && urlThreadId !== capturedThreadId) {
      setCapturedThreadId(urlThreadId);
      setCapturedAction(urlAction);
      setActiveTab("messages");
      setSearchParams({}, { replace: true });
    }
  }, [urlThreadId, capturedThreadId, urlAction, setSearchParams]);

  // Resolve ?openMessage=<userId> to a thread and open it on the messages tab.
  useEffect(() => {
    if (!urlOpenMessage || urlOpenMessage === handledOpenMessage) return;

    setHandledOpenMessage(urlOpenMessage);

    getOrCreateThread.mutate(urlOpenMessage, {
      onSuccess: (threadId) => {
        setCapturedThreadId(threadId);
        setCapturedAction(null);
        setActiveTab("messages");
        setSearchParams({}, { replace: true });
      },
      onError: () => {
        toast.error("Could not open that conversation.");
        setSearchParams({}, { replace: true });
      },
    });
  }, [urlOpenMessage, handledOpenMessage, getOrCreateThread, setSearchParams]);

  useEffect(() => {
    if (activeTab !== "messages") {
      setCapturedThreadId(null);
      setCapturedAction(null);
    }
  }, [activeTab]);

  const initialThreadId = capturedThreadId;
  const initialAction = capturedAction;

  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: userRoles } = useUserRoles();
  const queryClient = useQueryClient();
  const { data: unreadCount } = useThreadUnreadCount();
  const { data: pendingHelpCount } = usePendingHelpRequestCount();
  const { data: pendingMentorshipCount } = usePendingMentorshipRequestCount();
  const { data: pendingMentorOfferCount } = usePendingMentorOfferCount();
  const { data: receivedConnectionRequests } = useReceivedConnectionRequests();
  const communityRequestCount = receivedConnectionRequests?.length || 0;
  const [surveyDialogOpen, setSurveyDialogOpen] = useState(false);
  const [connectionToEnd, setConnectionToEnd] = useState<string | null>(null);
  const [satisfactionRating, setSatisfactionRating] = useState<number | null>(null);
  const [activeThreadInfo, setActiveThreadInfo] = useState<{
    threadId: string;
    otherPerson: { id: string; full_name: string; avatar_url: string | null };
  } | null>(null);

  const handleMessageClick = async (connection: ConnectionRequest) => {
    if (!user?.id) {
      toast.error("You must be logged in to message");
      return;
    }
    const isCurrentUserEntrepreneur = connection.entrepreneur.id === user.id;
    const otherPerson = isCurrentUserEntrepreneur ? connection.mentor : connection.entrepreneur;
    try {
      const threadId = await getOrCreateThread.mutateAsync(otherPerson.id);
      if (threadId) {
        setActiveThreadInfo({ threadId, otherPerson });
      } else {
        toast.error("Failed to open messaging");
      }
    } catch (error) {
      toast.error("Failed to open messaging: " + (error instanceof Error ? error.message : String(error)));
    }
  };

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [user, authLoading, navigate]);

  // Fetch mentorship connections from unified connections table
  const { data: connections, isLoading: connectionsLoading } = useQuery({
    queryKey: ["connections", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data: connectionsData, error } = await supabase
        .from("connections")
        .select("id, status, message, scheduled_at, zoom_link, created_at, connection_type, requester_id, recipient_id, mentorship_completed_at")
        .eq("connection_type", "mentorship")
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order("created_at", { ascending: false });

      if (error) throw error;
      if (!connectionsData) return [];

      const allUserIds = [...new Set(connectionsData.flatMap((c) => [c.requester_id, c.recipient_id].filter(Boolean)))];
      let profileMap = new Map<string, { id: string; full_name: string; avatar_url: string | null }>();
      let emailMap = new Map<string, string>();

      if (allUserIds.length > 0) {
        const [profilesRes, privateRes] = await Promise.all([
          supabase.from("profiles").select("id, full_name, avatar_url").in("id", allUserIds),
          supabase.from("profile_private").select("user_id, email").in("user_id", allUserIds),
        ]);
        profilesRes.data?.forEach((p) => profileMap.set(p.id, p));
        privateRes.data?.forEach((p) => emailMap.set(p.user_id, p.email));
      }

      return connectionsData.map((c) => ({
        id: c.id,
        status: c.status,
        message: c.message,
        scheduled_at: c.scheduled_at,
        zoom_link: c.zoom_link,
        created_at: c.created_at,
        mentorship_completed_at: c.mentorship_completed_at,
        entrepreneur: {
          id: c.requester_id,
          full_name: profileMap.get(c.requester_id)?.full_name || "Unknown",
          avatar_url: profileMap.get(c.requester_id)?.avatar_url || null,
          email: emailMap.get(c.requester_id),
        },
        mentor: {
          id: c.recipient_id,
          full_name: profileMap.get(c.recipient_id)?.full_name || "Unknown",
          avatar_url: profileMap.get(c.recipient_id)?.avatar_url || null,
          email: emailMap.get(c.recipient_id),
        },
      })) as ConnectionRequest[];
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const respondToRequest = useMutation({
    mutationFn: async ({ requestId, accept, connection }: { requestId: string; accept: boolean; connection?: ConnectionRequest }) => {
      // NOTE: `mentor_responded_at` does NOT exist on public.connections (it is a
      // column on the legacy archive table), so writing it returned PGRST204 and
      // Accept/Decline failed outright. The real column is accepted_at.
      const updates: Record<string, unknown> = {
        status: accept ? "accepted" : "declined",
        ...(accept ? { accepted_at: new Date().toISOString() } : {}),
      };
      if (accept && connection) {
        const zoomResponse = await supabase.functions.invoke("create-zoom-meeting", {
          body: { topic: `Mentorship Call: ${connection.entrepreneur.full_name} & ${connection.mentor.full_name}`, duration: 60 },
        });
        if (zoomResponse.error) throw new Error("Failed to create Zoom meeting");
        if (zoomResponse.data?.join_url) updates.zoom_link = zoomResponse.data.join_url;
      }
      const { error } = await supabase.from("connections").update(updates).eq("id", requestId);
      if (error) throw error;
    },
    onSuccess: (_, { accept }) => {
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      toast.success(accept ? "Connection accepted! A Zoom meeting has been created." : "Connection declined.");
    },
    onError: (error) => toast.error("Failed to respond: " + error.message),
  });

  const endConnection = useMutation({
    mutationFn: async ({ connectionId, rating }: { connectionId: string; rating: number }) => {
      const { error: surveyError } = await supabase
        .from("connection_satisfaction_surveys")
        .insert({ connection_id: connectionId, user_id: user?.id, satisfaction_rating: rating });
      if (surveyError) throw surveyError;
      const { error } = await supabase
        .from("connections")
        .update({ status: "declined", mentorship_completed_at: new Date().toISOString(), is_mentorship_active: false })
        .eq("id", connectionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      queryClient.invalidateQueries({ queryKey: ["weekly_requests"] });
      toast.success("Connection ended. Thank you for your feedback!");
      setSurveyDialogOpen(false);
      setConnectionToEnd(null);
      setSatisfactionRating(null);
    },
    onError: (error) => toast.error("Failed to end connection: " + error.message),
  });

  const handleEndConnectionClick = (connectionId: string) => {
    setConnectionToEnd(connectionId);
    setSatisfactionRating(null);
    setSurveyDialogOpen(true);
  };

  const handleSubmitSurvey = () => {
    if (!connectionToEnd || !satisfactionRating) return;
    endConnection.mutate({ connectionId: connectionToEnd, rating: satisfactionRating });
  };

  const scheduleCall = useMutation({
    mutationFn: async (connection: ConnectionRequest) => {
      const isCurrentUserEntrepreneur = connection.entrepreneur.id === user?.id;
      const otherPerson = isCurrentUserEntrepreneur ? connection.mentor : connection.entrepreneur;
      const currentPerson = isCurrentUserEntrepreneur ? connection.entrepreneur : connection.mentor;
      const response = await supabase.functions.invoke("send-scheduling-email", {
        body: {
          connectionId: connection.id,
          requesterId: user?.id,
          recipientId: otherPerson.id,
          requesterName: profile?.full_name || currentPerson.full_name,
          recipientEmail: otherPerson.email,
          recipientName: otherPerson.full_name,
        },
      });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => toast.success("Scheduling request sent!"),
    onError: (error) => toast.error("Failed to send scheduling request: " + error.message),
  });

  const isMentor = profile?.role === "mentor";
  const isEntrepreneur = profile?.role === "entrepreneur";
  const isAdmin = userRoles?.includes("admin") ?? false;
  const isInstructor = userRoles?.includes("instructor") ?? false;
  const showHelpRequests = isAdmin || isInstructor || isMentor;

  const filterSameNameConnections = (conn: ConnectionRequest) => {
    if (!conn.entrepreneur?.full_name || !conn.mentor?.full_name) return true;
    return conn.entrepreneur.full_name.toLowerCase().trim() !== conn.mentor.full_name.toLowerCase().trim();
  };

  // connections.status is connection_status_v2 = {pending, accepted, declined,
  // blocked}. This used to filter on legacy connection_status labels
  // (pending_admin / pending_mentor / completed), which match nothing — so the
  // pending section, the Accept/Decline buttons and the history never rendered.
  const pendingConnections = connections?.filter(
    (c) => filterSameNameConnections(c) && c.status === "pending"
  );
  const activeConnections = connections?.filter((c) => filterSameNameConnections(c) && c.status === "accepted");
  // NOTE: endConnection() marks a COMPLETED mentorship as status='declined' plus
  // mentorship_completed_at, so "declined" alone would also catch completed ones.
  // Keep the two lists mutually exclusive.
  const rejectedConnections = connections?.filter(
    (c) => filterSameNameConnections(c) && c.status === "declined" && !c.mentorship_completed_at
  );
  // "Completed" mentorships are flagged on the row, not by a status value.
  const pastConnections = connections?.filter(
    (c) => filterSameNameConnections(c) && !!c.mentorship_completed_at
  );

  // Combined mentorship badge count
  const mentorshipBadgeCount =
    (pendingMentorshipCount || 0) + (pendingMentorOfferCount || 0) + (pendingConnections?.length || 0) + (activeConnections?.length || 0);

  return (
    <DashboardLayout>
      {authLoading || profileLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : !user || !profile ? null : (
        <div className="space-y-6 animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold">My Connections</h1>
            <p className="text-muted-foreground">Manage your community and mentorship connections</p>
          </div>

          {/* ── Top-level tabs ── */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="flex-wrap h-auto gap-1">
              <TabsTrigger value="community">
                <Users className="h-4 w-4 mr-1.5" />
                Community
                {communityRequestCount > 0 && (
                  <Badge variant="secondary" className="ml-1.5 h-5 min-w-5 px-1 text-xs">
                    {communityRequestCount}
                  </Badge>
                )}
              </TabsTrigger>

              <TabsTrigger value="mentorship">
                <Handshake className="h-4 w-4 mr-1.5" />
                Mentorship
                {mentorshipBadgeCount > 0 && (
                  <Badge variant="secondary" className="ml-1.5 h-5 min-w-5 px-1 text-xs">
                    {mentorshipBadgeCount}
                  </Badge>
                )}
              </TabsTrigger>

              <TabsTrigger value="messages">
                <MessageSquare className="h-4 w-4 mr-1.5" />
                Messages
                {(unreadCount ?? 0) > 0 && (
                  <Badge variant="secondary" className="ml-1.5 h-5 min-w-5 px-1 text-xs">
                    {unreadCount}
                  </Badge>
                )}
              </TabsTrigger>

              {showHelpRequests && (
                <TabsTrigger value="help-requests">
                  <Hand className="h-4 w-4 mr-1.5" />
                  Help Requests
                  {(isAdmin || isInstructor) && (pendingHelpCount ?? 0) > 0 && (
                    <Badge variant="secondary" className="ml-1.5 h-5 min-w-5 px-1 text-xs">
                      {pendingHelpCount}
                    </Badge>
                  )}
                </TabsTrigger>
              )}
            </TabsList>

            {/* ── Community tab ── */}
            <TabsContent value="community" className="mt-6">
              <CommunityConnectionsSection />
            </TabsContent>

            {/* ── Mentorship tab — flat sections with headings ── */}
            <TabsContent value="mentorship" className="mt-6 space-y-8">
              {/* Active Mentorships — always first */}
              <section>
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  Active Mentorships
                  {(activeConnections?.length || 0) > 0 && (
                    <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs">
                      {activeConnections?.length}
                    </Badge>
                  )}
                </h2>
                {connectionsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : activeConnections && activeConnections.length > 0 ? (
                  <div className="space-y-4">
                    {activeConnections.map((connection) => (
                      <ConnectionCard
                        key={connection.id}
                        connection={connection}
                        currentUserId={user.id}
                        isMentor={isMentor}
                        onComplete={() => handleEndConnectionClick(connection.id)}
                        onMessage={() => handleMessageClick(connection)}
                        onScheduleCall={() => scheduleCall.mutate(connection)}
                        isPending={endConnection.isPending}
                        isScheduling={scheduleCall.isPending}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-4">No active mentorships</p>
                )}
              </section>

              {/* Pending Mentorship Connections (from connections table) */}
              {(pendingConnections?.length || 0) > 0 && (
                <section>
                  <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    Pending Connections
                    <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs">
                      {pendingConnections?.length}
                    </Badge>
                  </h2>
                  <div className="space-y-4">
                    {pendingConnections!.map((connection) => (
                      <ConnectionCard
                        key={connection.id}
                        connection={connection}
                        currentUserId={user.id}
                        isMentor={isMentor}
                        onAccept={() => respondToRequest.mutate({ requestId: connection.id, accept: true, connection })}
                        onDecline={() => respondToRequest.mutate({ requestId: connection.id, accept: false })}
                        isPending={respondToRequest.isPending}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Mentorship Requests (from mentorship_requests table) — role-aware */}
              {(isMentor || isEntrepreneur) && (
                <section>
                  <h2 className="text-lg font-semibold mb-3">Mentorship Requests</h2>
                  <div className="space-y-6">
                    {isMentor && (
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-2">Incoming Requests</h3>
                        <MentorshipRequestsList />
                      </div>
                    )}
                    {isEntrepreneur && (
                      <>
                        <div>
                          <h3 className="text-sm font-medium text-muted-foreground mb-2">Mentor Offers for You</h3>
                          <ReceivedMentorOffersList />
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-muted-foreground mb-2">Your Sent Requests</h3>
                          <SentMentorshipRequestsList />
                        </div>
                      </>
                    )}
                  </div>
                </section>
              )}

              {/* History — declined + completed */}
              {((rejectedConnections?.length || 0) > 0 || (pastConnections?.length || 0) > 0) && (
                <section>
                  <h2 className="text-lg font-semibold mb-3">History</h2>
                  <div className="space-y-6">
                    {rejectedConnections && rejectedConnections.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-2">Declined</h3>
                        <div className="space-y-4">
                          {rejectedConnections.map((connection) => (
                            <RejectedConnectionCard
                              key={connection.id}
                              connection={connection}
                              currentUserId={user.id}
                              isMentor={isMentor}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {pastConnections && pastConnections.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-2">Completed</h3>
                        <div className="space-y-4">
                          {pastConnections.map((connection) => (
                            <ConnectionCard
                              key={connection.id}
                              connection={connection}
                              currentUserId={user.id}
                              isMentor={isMentor}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}
            </TabsContent>

            {/* ── Messages tab ── */}
            <TabsContent value="messages" className="mt-6">
              <MessageThreadsList initialThreadId={initialThreadId} initialAction={initialAction} />
            </TabsContent>

            {/* ── Help Requests tab ── */}
            {showHelpRequests && (
              <TabsContent value="help-requests" className="mt-6">
                <HelpRequestsList />
              </TabsContent>
            )}
          </Tabs>

          {/* Satisfaction Survey Dialog */}
          <Dialog open={surveyDialogOpen} onOpenChange={setSurveyDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>End Connection</DialogTitle>
                <DialogDescription>How happy are you with this connection?</DialogDescription>
              </DialogHeader>
              <div className="py-6">
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5, 6, 7].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => setSatisfactionRating(rating)}
                      className={`w-10 h-10 rounded-full border-2 font-semibold transition-all ${
                        satisfactionRating === rating
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-muted-foreground/30 hover:border-primary/50"
                      }`}
                    >
                      {rating}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-2 px-1">
                  <span>Not satisfied</span>
                  <span>Very satisfied</span>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setSurveyDialogOpen(false); setConnectionToEnd(null); setSatisfactionRating(null); }}>
                  Cancel
                </Button>
                <Button onClick={handleSubmitSurvey} disabled={!satisfactionRating || endConnection.isPending}>
                  {endConnection.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Submit
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Messaging Panel Dialog */}
          <Dialog open={!!activeThreadInfo} onOpenChange={(open) => !open && setActiveThreadInfo(null)}>
            <DialogContent className="sm:max-w-2xl h-[80vh] flex flex-col p-0">
              {activeThreadInfo && (
                <ThreadMessagingPanel
                  threadId={activeThreadInfo.threadId}
                  otherPerson={activeThreadInfo.otherPerson}
                  onBack={() => setActiveThreadInfo(null)}
                />
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}
    </DashboardLayout>
  );
}

/* ── Sub-components ── */

function ConnectionCard({
  connection,
  currentUserId,
  isMentor,
  onAccept,
  onDecline,
  onComplete,
  onMessage,
  onScheduleCall,
  isPending,
  isScheduling,
}: {
  connection: ConnectionRequest;
  currentUserId: string;
  isMentor: boolean;
  onAccept?: () => void;
  onDecline?: () => void;
  onComplete?: () => void;
  onMessage?: () => void;
  onScheduleCall?: () => void;
  isPending?: boolean;
  isScheduling?: boolean;
}) {
  const isCurrentUserEntrepreneur = connection.entrepreneur.id === currentUserId;
  const otherPerson = isCurrentUserEntrepreneur ? connection.mentor : connection.entrepreneur;
  const isActive = connection.status === "accepted";
  // The recipient of a pending request is the one who can respond.
  const needsResponse =
    connection.status === "pending" && connection.mentor.id === currentUserId;
  const hasFutureScheduledCall = connection.scheduled_at && isFuture(new Date(connection.scheduled_at));

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">Pending</Badge>;
      case "blocked":
        return <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">Blocked</Badge>;
      case "accepted":
        return <Badge variant="outline" className="bg-success/10 text-success border-success/30">Active</Badge>;
      case "completed":
        return <Badge variant="outline">Completed</Badge>;
      case "declined":
        return <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">Declined</Badge>;
      case "rejected_admin":
        return <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">Not Approved</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <Avatar className="h-12 w-12">
              <AvatarImage src={otherPerson.avatar_url || undefined} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {otherPerson.full_name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium"><UserName userId={otherPerson.id} name={otherPerson.full_name} /></p>
              <p className="text-sm text-muted-foreground">{format(new Date(connection.created_at), "MMM d, yyyy")}</p>
            </div>
            {getStatusBadge(connection.status)}
          </div>

          {connection.message && (
            <div className="bg-muted p-3 rounded-lg md:max-w-sm">
              <p className="text-sm italic">"{connection.message}"</p>
            </div>
          )}

          <div className="flex items-center gap-2">
            {needsResponse && onAccept && onDecline && (
              <>
                <Button size="sm" variant="outline" onClick={onDecline} disabled={isPending}>
                  <X className="h-4 w-4 mr-1" />
                  Decline
                </Button>
                <Button size="sm" onClick={onAccept} disabled={isPending} className="bg-accent hover:bg-accent/90">
                  {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                  Accept
                </Button>
              </>
            )}

            {isActive && (
              <div className="flex items-center gap-2">
                {onMessage && (
                  <Button size="sm" variant="outline" onClick={onMessage}>
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Message
                  </Button>
                )}
                {onScheduleCall && (
                  <Button size="sm" variant="outline" onClick={onScheduleCall} disabled={isScheduling}>
                    {isScheduling ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Calendar className="h-4 w-4 mr-1" />}
                    Schedule Call
                  </Button>
                )}
                {onComplete && !hasFutureScheduledCall && (
                  <Button size="sm" variant="outline" onClick={onComplete} disabled={isPending}>
                    <X className="h-4 w-4 mr-1" />
                    End Connection
                  </Button>
                )}
                {onComplete && hasFutureScheduledCall && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button size="sm" variant="outline" disabled>
                            <X className="h-4 w-4 mr-1" />
                            End Connection
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Cancel your scheduled call first before ending this connection</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            )}

            {!!connection.mentorship_completed_at && (
              <Button size="sm" variant="outline" disabled>
                <RefreshCw className="h-4 w-4 mr-1" />
                Request Another
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RejectedConnectionCard({
  connection,
  currentUserId,
  isMentor,
}: {
  connection: ConnectionRequest;
  currentUserId: string;
  isMentor: boolean;
}) {
  const isCurrentUserEntrepreneur = connection.entrepreneur.id === currentUserId;
  const otherPerson = isCurrentUserEntrepreneur ? connection.mentor : connection.entrepreneur;

  return (
    <Card className="border-destructive/20">
      <CardContent className="pt-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Avatar className="h-12 w-12">
              <AvatarImage src={otherPerson.avatar_url || undefined} />
              <AvatarFallback>{otherPerson.full_name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium"><UserName userId={otherPerson.id} name={otherPerson.full_name} /></p>
              <p className="text-sm text-muted-foreground">{isMentor ? "Entrepreneur" : "Mentor"}</p>
            </div>
          </div>
          <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30 w-fit">
            Declined
          </Badge>
        </div>
        {connection.admin_rejection_reason_public && (
          <div className="mt-4 p-3 bg-muted rounded-lg border-l-4 border-destructive/50">
            <p className="text-sm font-medium mb-1">Reason:</p>
            <p className="text-sm text-muted-foreground">{connection.admin_rejection_reason_public}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12">
        <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">{message}</p>
      </CardContent>
    </Card>
  );
}
